import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = typeof obj?.[key] === 'object' ? toCamelCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);

  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = typeof obj?.[key] === 'object' ? toSnakeCase(obj?.[key]) : obj?.[key];
    return acc;
  }, {});
};

export const hotspotService = {
  // Get hotspots for a catalog
  async getHotspotsByCatalog(catalogId) {
    try {
      const { data, error } = await supabase?.from('hotspots')?.select('*')?.eq('catalog_id', catalogId)?.order('page_number', { ascending: true });

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching hotspots:', error?.message);
      throw error;
    }
  },

  // Get hotspots for a specific page
  async getHotspotsByPage(catalogId, pageNumber) {
    try {
      const { data, error } = await supabase?.from('hotspots')?.select('*')?.eq('catalog_id', catalogId)?.eq('page_number', pageNumber);

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error fetching page hotspots:', error?.message);
      throw error;
    }
  },

  // Create hotspot
  async createHotspot(hotspotData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const snakeData = toSnakeCase({
        ...hotspotData,
        createdBy: user?.id
      });

      const { data, error } = await supabase?.from('hotspots')?.insert(snakeData)?.select()?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error creating hotspot:', error?.message);
      throw error;
    }
  },

  // Update hotspot
  async updateHotspot(hotspotId, hotspotData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const snakeData = toSnakeCase(hotspotData);

      const { data, error } = await supabase?.from('hotspots')?.update(snakeData)?.eq('id', hotspotId)?.select()?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error updating hotspot:', error?.message);
      throw error;
    }
  },

  // Delete hotspot
  async deleteHotspot(hotspotId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase?.from('hotspots')?.delete()?.eq('id', hotspotId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting hotspot:', error?.message);
      throw error;
    }
  },

  // Toggle hotspot visibility
  async toggleVisibility(hotspotId, visible) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('hotspots')?.update({ visible })?.eq('id', hotspotId)?.select()?.single();

      if (error) throw error;
      return toCamelCase(data);
    } catch (error) {
      console.error('Error toggling hotspot visibility:', error?.message);
      throw error;
    }
  }
};